import { AmountWithUnit } from 'components/shared/Amount/Amount';
import { ColoredToggleButton } from 'components/shared/ColoredToggleButton';
import { CompactSelector } from 'components/shared/compact-selector';
import { useSalesCounterpartButton } from 'components/shared/compact-selector/use-toggle-button';
import { SalesCounterPartResult } from 'components/shared/SalesCounterPart/SalesCounterPartResult';
import { SelectSearch } from 'components/shared/SelectSearch/SelectSearch';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { FC, useCallback } from 'react';
import type { Observable } from 'rxjs';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { DefaultFieldValues } from 'state/userPreferences/userPreferences.model';
import styled from 'styled-components';

import type { DefaultFieldKey } from '../UserPreferencesForm.connect';
import { FlexRowGroup, GridRowGroup } from './RowGroup';
import { SectionTitle, TabForm } from './TabForm';

interface ClientTabProps {
  onClose: () => void;
  isPrivateMode: boolean;
  xMargin: Nullable<number>;
  onClientSearch: (text: string) => Observable<ISalesCounterpart[]>;
  selectedOnBehalfClient: Nullable<ISalesCounterpart>;
  onDefaultFieldChanged<K extends DefaultFieldKey>(key: K, value: DefaultFieldValues[K]): void;
}

const StyledDiv = styled.div`
  width: 10rem;
  float: right;
`;
export const ClientTabForm: FC<ClientTabProps> = ({
  onClose,
  isPrivateMode,
  onDefaultFieldChanged,
  onClientSearch,
  selectedOnBehalfClient,
  xMargin,
}) => {
  const setIsPrivateMode = useCallback(
    (value: any) => {
      logAnalytics.trackEvent('User preferences', 'Set private mode', value ? 'on' : 'off');
      return onDefaultFieldChanged('isPrivateMode', value);
    },
    [onDefaultFieldChanged],
  );

  const setClient = useCallback(
    (value: any) => {
      logAnalytics.trackEvent('User preferences', 'Set default client');
      if (value !== null) {
        onDefaultFieldChanged('xMargin', undefined);
      }
      return onDefaultFieldChanged('selectedOnBehalfClient', value);
    },
    [onDefaultFieldChanged],
  );

  const setXMargin = useCallback(
    (value: any) => {
      logAnalytics.trackEvent('User preferences', 'Set default xMargin');
      return onDefaultFieldChanged('xMargin', value);
    },
    [onDefaultFieldChanged],
  );
  const { label: clientLabel, className } = useSalesCounterpartButton(selectedOnBehalfClient, true);
  return (
    <TabForm labelKey="userPreferencesForm.client.label" onClose={onClose}>
      <SectionTitle labelKey="userPreferencesForm.defaultValues.label" />
      <FlexRowGroup labelKey="userPreferencesForm.isPrivateMode.label" isFirst>
        <ColoredToggleButton
          id="isPrivateMode"
          iconName={isPrivateMode ? 'visibility_off' : 'visibility'}
          labelKey={isPrivateMode ? 'form.IsPrivateMode' : 'form.IsPrivateMode.false'}
          value={isPrivateMode ?? false}
          isDisabled={false}
          onValueChanged={setIsPrivateMode}
          className="border-0"
          e2e="IsPrivateMode"
        />
      </FlexRowGroup>
      <GridRowGroup labelKey="userPreferencesForm.client.label" contentCol="10" labelCol="2">
        <CompactSelector
          popupPlacement="bottom"
          onSelectedItemChanged={setClient}
          selectedValue={selectedOnBehalfClient}
          renderButton={(ref, onClick) => (
            <CompactSelector.ToggleButton
              e2ePrefix="client"
              ref={ref}
              onClick={onClick}
              label={clientLabel}
              className={className}
            />
          )}
          renderContent={onSelectedChanged => (
            <SelectSearch
              autofocus
              getSearchResult={onClientSearch}
              onValueSelected={onSelectedChanged}
              resultRenderer={SalesCounterPartResult}
            />
          )}
        />
      </GridRowGroup>
      <FlexRowGroup labelKey="userPreferencesForm.xMargin.label" isFirst={false}>
        <StyledDiv>
          <AmountWithUnit
            e2e="user-pref-xMargin"
            incrementStep={0.1}
            onBlur={setXMargin}
            placeholder=""
            unit="%"
            min={0}
            value={xMargin}
            precision={4}
            isReadOnly={selectedOnBehalfClient !== null}
            hasError={false}
          />
        </StyledDiv>
      </FlexRowGroup>
    </TabForm>
  );
};

ClientTabForm.displayName = 'ClientTabForm';
